'use client'

import { BuilderStoreContext } from '@builder.io/react';
import useSearchspring from 'components/hooks/use-searchspring';
import { type SearchResponseModel } from 'int-searchspring-api';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  FacetProps,
  SearchProductResult,
  SortFunction,
  type RefinementFunction
} from 'propel-react-components';
import { LinkComponentInterface } from 'propel-shared-utility';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getSearchspringState, withUpdateSearchParameters } from './search-spring-state';
import { useRefinementFunction } from './use-refinement-function';
import { useSortFunction } from './use-sort-function';

type SearchspringSearchProps<T> = T & {
  refinementFunction?: RefinementFunction;
  sortFunction?: SortFunction;
  searchResult?: {
    facets: FacetProps[];
    results: SearchProductResult[];
  } | SearchResponseModel;
  tilePromo?: boolean;
  tileBurst?: boolean;
  tileBanner?: boolean;
  tileTagline?: boolean;
  tileSpecs?: boolean;
  tileRatings?: boolean;
  tileATC?: boolean;
  mobile?: boolean;
  LinkComponent?: LinkComponentInterface;
};

export const withSearchspringSearch = <T,>(
  Component: React.FC<SearchspringSearchProps<T>>,
): React.FC<SearchspringSearchProps<T>> => {
  const WrappedComponent = (props: SearchspringSearchProps<T>) => {
    const urlPathBase = process.env.NEXT_PUBLIC_DOMAIN_NAME;
    const pathname = usePathname() || '';
    const searchParams = useSearchParams()
    const [error, setError] = useState<any>(null);
    const sortFunction = useSortFunction();
    const refinementFunction = useRefinementFunction();
    const builderState = useContext(BuilderStoreContext);
    const view = searchParams?.get('view');
    const contentCategoryHandle = builderState?.content?.data?.handle;
    const productCategoryHandle = builderState?.state?.collection?.handle
    const productCategoryTitle = builderState?.state?.collection?.title;
    const [contentType, setContentType] = useState(view === 'articles' || contentCategoryHandle ? 'articles' : 'products');

    useEffect(() => {
      setContentType(view === 'articles' || contentCategoryHandle ? 'articles' : 'products');
    }, [view, contentCategoryHandle]);

    const tileOptions = {
      banner: props.tileBanner,
      burst: props.tileBurst,
      promo: props.tilePromo,
      specs: props.tileSpecs,
      ratings: props.tileRatings,
      tagline: props.tileTagline,
      addToCart: props.tileATC,
    };

    const getState = useCallback(() => {
      const searchValue: { [key: string]: string } = {};
      const filterParams: { [key: string]: string[] } = {};
      const sortParams: { [key: string]: string } = {};
      const filterParamsProducts: Record<string, string[]> = {};
      const filterParamsArticles: Record<string, string[]> = {};
      const sortParamsProducts: Record<string, string> = {};
      const sortParamsArticles: Record<string, string> = {};

      if (searchParams) {
        for (const [key, value] of searchParams.entries()) {
          if (key === 'q') {
            searchValue[key] = value;
          } else if (key.startsWith('filter.')) {
            const filterKey = key.replace('filter.', '');
            if (contentType === 'products') {
              if (!filterParamsProducts[`filter.${filterKey}`]) {
                filterParamsProducts[`filter.${filterKey}`] = [];
              }
              filterParamsProducts[`filter.${filterKey}`]?.push(value);
            } else if (contentType === 'articles') {
              if (!filterParamsArticles[`filter.${filterKey}`]) {
                filterParamsArticles[`filter.${filterKey}`] = [];
              }
              filterParamsArticles[`filter.${filterKey}`]?.push(value);
            } else {
              if (!filterParams[key]) {
                filterParams[key] = [];
              }
              filterParams[key]?.push(value);
            }
          } else if (key.startsWith('sort.')) {
            const sortKey = key.replace('sort.', '');
            if (contentType === 'products') {
              sortParamsProducts[`sort.${sortKey}`] = value;
            } else if (contentType === 'articles') {
              sortParamsArticles[`sort.${sortKey}`] = value;
            } else {
              sortParams[key] = value;
            }
          }
        }
      }

      let articlesState = getSearchspringState({ contentType: 'articles', overrides: { bgfilter: contentCategoryHandle ? { "category_handle": [contentCategoryHandle] } : undefined } });
      let productsState = getSearchspringState({ contentType: 'product', overrides: { bgfilter: productCategoryHandle ? { "category_ids": [productCategoryHandle] } : undefined } });

      const queryStringProducts = constructQueryString({ ...filterParamsProducts, ...sortParamsProducts });
      const queryStringArticles = constructQueryString({ ...filterParamsArticles, ...sortParamsArticles });
      const queryStringSingleUrl = constructQueryString({ ...filterParams, ...sortParams })

      const urlPathProducts = `${urlPathBase}${pathname}?${searchValue.q ? `q=${searchValue.q}&` : ''}${queryStringProducts}`;
      const urlPathArticles = `${urlPathBase}${pathname}?${searchValue.q ? `q=${searchValue.q}&` : ''}${queryStringArticles}`;
      const urlPath = `${urlPathBase}${pathname}?${searchValue.q ? `q=${searchValue.q}&` : ''}${queryStringSingleUrl}`;

      if (contentType === 'products') {
        productsState = withUpdateSearchParameters(productsState, urlPathProducts);
        articlesState = withUpdateSearchParameters(articlesState, urlPathArticles);
      } else if (contentType === 'articles') {
        productsState = withUpdateSearchParameters(productsState, urlPathProducts);
        articlesState = withUpdateSearchParameters(articlesState, urlPathArticles);
      } else {
        productsState = withUpdateSearchParameters(productsState, urlPath);
        articlesState = withUpdateSearchParameters(articlesState, urlPath);
      }
      return { productsState, articlesState }
    }, [contentType, contentCategoryHandle, productCategoryHandle, searchParams, urlPathBase, pathname])

    const { productsState, articlesState } = getState();

    const articleResults = useSearchspring({ state: articlesState });
    const productResults = useSearchspring({ state: productsState });

    const prevRefinementFunction = useRef(refinementFunction);

    const articleDataCount = Number(articleResults.currentPage?.pagination?.totalResults ?? 0);
    const productDataCount = Number(productResults.currentPage?.pagination?.totalResults ?? 0);

    const isArticlesView = contentType === 'articles';
    const searchResults = (isArticlesView) ? articleResults : productResults;
    const searchData = searchResults.currentPage;
    const allResults = searchResults.allResults;

    prevRefinementFunction.current = refinementFunction;

    const getCurrentPage = useCallback(() => {
      if (contentType === 'articles') {
        return articleResults.page;
      } else {
        return productResults.page;
      }
    }, [contentType, articleResults.page, productResults.page]);

    const setCurrentPage = useCallback((nextPage: number) => {
      if (contentType === 'articles') {
        articleResults.setPage(nextPage);
      } else {
        productResults.setPage(nextPage);
      }
    }, [contentType, articleResults, productResults]);

    const handleLoadMore = useCallback(() => {
      const nextPage = getCurrentPage() + 1;
      setCurrentPage(nextPage);
    }, [getCurrentPage, setCurrentPage]);

    return (
      <Component {...props}
        contentCategoryHandle={contentCategoryHandle}
        productCategoryHandle={productCategoryHandle}
        articleDataCount={articleDataCount}
        productDataCount={productDataCount}
        setContentType={setContentType}
        sorting={searchData}
        sortFunction={sortFunction}
        onLoadMore={handleLoadMore}
        refinementFunction={refinementFunction}
        searchData={searchData}
        allResults={allResults}
        tileOptions={tileOptions}
        contentType={contentType}
        productCategoryTitle={productCategoryTitle}
        searchTerm={searchParams.get('q')}
      />
    );
  };

  WrappedComponent.displayName = 'WithSearchspringSearch';

  return WrappedComponent;
};

const constructQueryString = (params: Record<string, string | string[]>) => {
  const queryStringFormat = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      value.forEach((item) => queryStringFormat.append(key, item));
    } else {
      queryStringFormat.append(key, value);
    }
  }

  return queryStringFormat.toString();
};

